'use client';

import { c as _c } from "react/compiler-runtime";
import { cn } from '@eversports/klimt-design-components/tailwind/utils';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import { Circle } from 'lucide-react';
import * as React from 'react';
const RadioGroup = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Root>, React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>>((t0, ref) => {
  const $ = _c(9);
  let className;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
  } else {
    className = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== className) {
    t1 = cn("gap-1 grid", className);
    $[3] = className;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== props || $[6] !== ref || $[7] !== t1) {
    t2 = <RadioGroupPrimitive.Root className={t1} {...props} ref={ref} />;
    $[5] = props;
    $[6] = ref;
    $[7] = t1;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  return t2;
});
RadioGroup.displayName = RadioGroupPrimitive.Root.displayName;
const radioGroupItemVariants = cva('text-text-brand-primary border-border-enabled hover:border-border-active hover:bg-surface-primary data-[state=checked]:border-border-enabled data-[disabled]:text-text-disabled rounded-lg flex cursor-pointer items-center justify-center border outline-hidden data-disabled:cursor-not-allowed data-disabled:opacity-50', {
  variants: {
    size: {
      sm: 'size-2',
      md: 'size-3'
    }
  },
  defaultVariants: {
    size: 'sm'
  }
});
interface RadioGroupItemProps extends React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>, VariantProps<typeof radioGroupItemVariants> {}
const RadioGroupItem = React.forwardRef<React.ElementRef<typeof RadioGroupPrimitive.Item>, RadioGroupItemProps>((t0, ref) => {
  const $ = _c(16);
  let className;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = props;
    $[3] = t1;
  } else {
    className = $[1];
    props = $[2];
    t1 = $[3];
  }
  const size = t1 === undefined ? "sm" : t1;
  const circleSize = size === "sm" ? "size-1.5" : "size-[20px]";
  let t2;
  if ($[4] !== className || $[5] !== size) {
    t2 = cn(radioGroupItemVariants({
      size
    }), className);
    $[4] = className;
    $[5] = size;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== circleSize) {
    t3 = cn(circleSize, "fill-current");
    $[7] = circleSize;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  let t4;
  if ($[9] !== t3) {
    t4 = <RadioGroupPrimitive.Indicator className="flex items-center justify-center"><Circle className={t3} /></RadioGroupPrimitive.Indicator>;
    $[9] = t3;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== props || $[12] !== ref || $[13] !== t2 || $[14] !== t4) {
    t5 = <RadioGroupPrimitive.Item ref={ref} className={t2} {...props}>{t4}</RadioGroupPrimitive.Item>;
    $[11] = props;
    $[12] = ref;
    $[13] = t2;
    $[14] = t4;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
});
RadioGroupItem.displayName = RadioGroupPrimitive.Item.displayName;
export { RadioGroup, RadioGroupItem };